import React, { useEffect, useRef, useMemo } from 'react';
import { Animated, View, StyleSheet } from 'react-native';
import { useAppTheme } from '../contexts/ThemeContext';

export function SkeletonPulse({ style }) {
  const { colors, radius } = useAppTheme();
  const baseStyles = useMemo(() => createBaseStyles(colors, radius), [colors]);

  const anim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(anim, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(anim, { toValue: 0, duration: 900, useNativeDriver: true }),
      ])
    ).start();
  }, [anim]);

  const opacity = anim.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.7] });

  return (
    <Animated.View
      style={[baseStyles.base, style, { opacity }]}
    />
  );
}

export function QuoteCardSkeleton() {
  const { colors, spacing, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, radius), [colors]);

  return (
    <View style={styles.card}>
      <View style={styles.cardTop}>
        <SkeletonPulse style={styles.avatar} />
        <View style={styles.cardTopRight}>
          <SkeletonPulse style={styles.lineLg} />
          <SkeletonPulse style={styles.lineSm} />
        </View>
        <SkeletonPulse style={styles.badge} />
      </View>
      <SkeletonPulse style={styles.lineMd} />
      <View style={styles.cardBottom}>
        <SkeletonPulse style={styles.lineXs} />
        <SkeletonPulse style={styles.lineXs} />
      </View>
    </View>
  );
}

export function InvoiceRowSkeleton() {
  const { colors, spacing, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, radius), [colors]);

  return (
    <View style={styles.row}>
      <View style={styles.rowLeft}>
        <SkeletonPulse style={styles.lineLg} />
        <SkeletonPulse style={[styles.lineSm, { marginTop: 6 }]} />
      </View>
      <View style={styles.rowRight}>
        <SkeletonPulse style={styles.lineAmount} />
        <SkeletonPulse style={[styles.badge, { marginTop: 6 }]} />
      </View>
    </View>
  );
}

export function ClientRowSkeleton() {
  const { colors, spacing, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, radius), [colors]);

  return (
    <View style={styles.clientCard}>
      <SkeletonPulse style={styles.clientAvatar} />
      <View style={{ flex: 1 }}>
        <SkeletonPulse style={styles.lineLg} />
        <SkeletonPulse style={[styles.lineSm, { marginTop: 6 }]} />
      </View>
      <SkeletonPulse style={styles.lineXs} />
    </View>
  );
}

export function DashboardSkeleton() {
  const { colors, spacing, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, radius), [colors]);

  return (
    <View style={{ gap: spacing.base, paddingHorizontal: spacing.xl }}>
      {/* Stats row */}
      <View style={styles.statsRow}>
        <SkeletonPulse style={styles.statCard} />
        <SkeletonPulse style={styles.statCard} />
        <SkeletonPulse style={styles.statCard} />
      </View>
      {/* Chart */}
      <SkeletonPulse style={styles.chartCard} />
      {/* Quote cards */}
      {[1, 2, 3].map((i) => <QuoteCardSkeleton key={i} />)}
    </View>
  );
}

function createBaseStyles(colors, radius) {
  return StyleSheet.create({
    base: {
      backgroundColor: colors.surfaceElevated,
      borderRadius: radius.sm,
    },
  });
}

function createStyles(colors, spacing, radius) {
  return StyleSheet.create({
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.base,
      marginBottom: spacing.md,
      gap: spacing.sm,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.base,
      marginBottom: spacing.sm,
    },
    rowLeft: { flex: 1, gap: 4 },
    rowRight: { alignItems: 'flex-end' },
    cardTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
    cardTopRight: { flex: 1, gap: 6 },
    cardBottom: { flexDirection: 'row', justifyContent: 'space-between' },
    avatar: { width: 42, height: 42, borderRadius: 21 },
    clientAvatar: { width: 42, height: 42, borderRadius: 21, marginRight: spacing.md },
    badge: { width: 60, height: 22, borderRadius: radius.full },
    lineLg: { height: 14, width: '70%', borderRadius: radius.sm },
    lineMd: { height: 12, width: '90%', borderRadius: radius.sm },
    lineSm: { height: 11, width: '40%', borderRadius: radius.sm },
    lineXs: { height: 10, width: '25%', borderRadius: radius.sm },
    lineAmount: { height: 14, width: 70, borderRadius: radius.sm },
    clientCard: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.base,
      marginBottom: spacing.md,
    },
    statsRow: { flexDirection: 'row', gap: spacing.sm },
    statCard: { flex: 1, height: 80, borderRadius: radius.md },
    chartCard: { height: 180, borderRadius: radius.lg },
  });
}
